// Stránky investičních bytů – navázání obou kalkulaček na DOM (růst hodnoty u Holzovy, výnos
// z nájmu u Horní Lipové). Čisté výpočty jsou v calc.js (testováno bez DOM tests/redesign/
// reality-calc.test.mjs), tenhle modul jen čte formulářové prvky a přepisuje [data-re-calc-out] /
// [data-re-calc-rows] výsledky. Vstupní data (sazby, výchozí hodnoty, ceny a plochy volných
// jednotek) dostává z PHP přes window.EFI_REALITY_CALC – viz komentář v investice.php, proč to jde
// jen díky tomu, že reality.js je zařazený do patičky.

import { computeRent, computeGrowth, czk, pct, num, pctSpace } from './calc';

function setOut(root, key, text) {
  root.querySelectorAll(`[data-re-calc-out="${key}"]`).forEach((el) => {
    el.textContent = text;
  });
}

function findUnit(units, no) {
  return units.find((u) => String(u.no) === String(no)) || units[0] || null;
}

/** "rok"/"roky"/"let" podle počtu – gramatika tady, samotná slova z `params.year_words` (calc.year_words v datech). */
function yearWord(n, words) {
  if (!Array.isArray(words) || words.length < 3) {
    return '';
  }

  if (n === 1) {
    return words[0];
  }

  return n >= 2 && n <= 4 ? words[1] : words[2];
}

/**
 * Zhodnocení hodnoty vůči kupní ceně, se znaménkem plus jako v návrhu ("+5,3 %"). Čistě zobrazovací
 * dopočet nad tím, co `computeGrowth()` z calc.js už vrací (`row.total`/`price`) – calc.js se
 * neupravuje, procento nikdy nepotřebovalo vlastní vzorec navíc.
 */
function plusPct(value, price) {
  if (!price) {
    return '—';
  }

  return `+${pct((value / price) * 100)}`;
}

/**
 * Peněžní hodnota se znaménkem plus jako v návrhu ("+1 776 767 Kč") – jen u nárůstu ceny (`endGain`/
 * `row.gain`) a celkového součtu (`total`/`row.total`). Nájem (`rentSum`/`row.rent`) plus v návrhu
 * nemá, tam se `czk()` volá bez tohohle wrapperu.
 */
function plusCzk(value) {
  return `+${czk(value)}`;
}

/** Kalkulačka růstu hodnoty (Holzova) – byt + délka nájmu sdílené oběma panely, plus posuvník růstu. */
function initGrowth(root, params, units) {
  const unitSelect = root.querySelector('[data-re-calc-unit]');
  const yearsInputs = root.querySelectorAll('[name="re-calc-years"]');
  const growthInput = root.querySelector('[data-re-calc-growth]');
  const rowsBody = root.querySelector('[data-re-calc-rows]');

  if (!unitSelect) {
    return;
  }

  const currentYears = () => {
    const checked = Array.from(yearsInputs).find((input) => input.checked);

    return checked ? Number(checked.value) : Number(params.years_default) || 1;
  };

  const render = () => {
    const unit = findUnit(units, unitSelect.value);
    const years = currentYears();
    const growth = growthInput ? Number(growthInput.value) : 0;

    const r = computeGrowth({ price: unit ? unit.price : null, years, growth, rate: params.rate });

    setOut(root, 'price', czk(r.price));
    setOut(root, 'month', czk(r.month));
    setOut(root, 'year', czk(r.year));
    setOut(root, 'endPrice', czk(Math.round(r.endPrice)));
    setOut(root, 'endGain', plusCzk(Math.round(r.endGain)));
    setOut(root, 'rentSum', czk(r.rentSum));
    setOut(root, 'total', plusCzk(Math.round(r.total)));
    // „5 %“ / „5,5 %“ jako v návrhu (growRateLabel: toLocaleString bez pevných desetin)
    setOut(root, 'growthLabel', `${Number(growth).toLocaleString(document.documentElement.lang || 'cs-CZ', { maximumFractionDigits: 1 })}${pctSpace()}%`);

    // Tři dlaždice souhrnu mají pod hodnotou ještě zhodnocení v procentech kupní ceny (návrh:
    // growPricePct/growRentPct/growTotalPct) – dlaždice "Cena bytu po…" ho v návrhu nemá.
    setOut(root, 'endGainPct', plusPct(r.endGain, r.price));
    setOut(root, 'rentSumPct', plusPct(r.rentSum, r.price));
    setOut(root, 'totalPct', plusPct(r.total, r.price));

    // Dvě dlaždice mají popisek závislý na zvolené délce nájmu ("Celkem za 5 let", "Cena bytu po
    // 5 letech") – skládá se tady z předpony v datech (`tile_total_label`/`label_end_price`) a slov,
    // co se skloňují podle počtu (`year_words` gramaticky, `after_years` nepravidelně po pádech).
    setOut(root, 'totalLabel', [params.tile_total_label, years, yearWord(years, params.year_words)].filter(Boolean).join(' '));

    const yearsList = Array.isArray(params.years) ? params.years : [];
    const yearsIndex = yearsList.indexOf(years);
    const afterPhrase = Array.isArray(params.after_years) && yearsIndex > -1 ? params.after_years[yearsIndex] : '';

    setOut(root, 'endPriceLabel', [params.label_end_price, afterPhrase].filter(Boolean).join(' '));

    if (rowsBody) {
      // Tabulka má vždy všechny roky (návrh: growRows [1–5]) – zvolená délka nájmu je zvýrazněná,
      // pozdější roky vyšedlé. Souhrn nad tabulkou (r) zůstává pro zvolenou délku.
      const maxYears = yearsList.length ? Math.max(...yearsList) : years;
      const table = computeGrowth({ price: unit ? unit.price : null, years: maxYears, growth, rate: params.rate });

      rowsBody.textContent = '';
      table.rows.forEach((row) => {
        const tr = document.createElement('tr');
        if (row.year === years) {
          tr.className = 'is-current';
        } else if (row.year > years) {
          tr.className = 'is-later';
        }
        // Šestý sloupec (Zhodnocení) je procento řádkového total vůči kupní ceně jednotky – stejně
        // jako growColPct v návrhu (pctOf(total), dělí se `cu.priceN`, ne řádkovou cenou po letech).
        const cells = [
          `${row.year}. ${yearWord(1, params.year_words)}`,
          czk(Math.round(row.price)),
          plusCzk(Math.round(row.gain)),
          czk(row.rent),
          plusCzk(Math.round(row.total)),
          plusPct(row.total, r.price),
        ];

        cells.forEach((value, i) => {
          const td = document.createElement('td');

          td.textContent = String(value);
          if (i === 0) {
            td.className = 're-calc__year';
          } else if (i === 4) {
            td.className = 're-calc__total'; // Celkem – zeleně tučně (návrh)
          }
          tr.appendChild(td);
        });
        rowsBody.appendChild(tr);
      });
    }
  };

  unitSelect.addEventListener('change', render);
  yearsInputs.forEach((input) => input.addEventListener('change', render));
  if (growthInput) {
    growthInput.addEventListener('input', render);
  }
  render();
}

/** Kalkulačka výnosu z nájmu (Horní Lipová) – dlouhodobý nájem (od e-Finance nebo vlastní) i krátkodobý provoz. */
function initRent(root, params, units) {
  const unitSelect = root.querySelector('[data-re-calc-unit]');
  const modeInputs = root.querySelectorAll('[name="re-calc-mode"]');
  const rentInput = root.querySelector('[data-re-calc-rent]');
  const adrInput = root.querySelector('[data-re-calc-adr]');
  const occInput = root.querySelector('[data-re-calc-occ]');
  const longPanel = root.querySelector('[data-re-calc-panel="long"]');
  const shortPanel = root.querySelector('[data-re-calc-panel="short"]');

  if (!unitSelect) {
    return;
  }

  // null = použije se nabídka e-Finance (efiRent); jakmile uživatel do pole napíše vlastní číslo,
  // drží se dokud nezmění byt (stejně jako reference apartmanyjeseniky.cz/js/app.js).
  let customRent = null;

  const currentMode = () => {
    const checked = Array.from(modeInputs).find((input) => input.checked);

    return checked ? checked.value : 'long';
  };

  const render = () => {
    const unit = findUnit(units, unitSelect.value);
    const mode = currentMode();
    const isShort = mode === 'short';

    if (longPanel) {
      longPanel.hidden = isShort;
    }
    if (shortPanel) {
      shortPanel.hidden = !isShort;
    }
    // poznámka k výpočtu vpravo pod dlaždicemi – jiná pro dlouhodobý nájem a krátkodobé ubytování
    root.querySelectorAll('[data-re-calc-note]').forEach((note) => {
      note.hidden = note.dataset.reCalcNote !== mode;
    });

    const r = computeRent({
      price: unit ? unit.price : null,
      mode,
      rent: customRent,
      adr: adrInput ? adrInput.value : null,
      occ: occInput ? occInput.value : null,
      efiYield: params.efi_yield,
      shortCostShare: params.short_cost_share,
      rentRound: params.rent_round,
    });

    if (rentInput && document.activeElement !== rentInput) {
      rentInput.value = customRent == null ? r.efiRent : customRent;
    }

    setOut(root, 'price', czk(r.price));
    // Bez "Kč" a bez jednotky "měsíc" – obojí je v markupu vedle (`Kč / <?= $re_calc['month_label']
    // ?>`, jen Horní Lipová), "měsíc" jde z dat (calc.month_label, viz investice.php), "Kč" je
    // universální zkratka jako jinde v tématu. Stejný rozklad jako reference
    // apartmanyjeseniky.cz/js/app.js (num(efiRent) + " Kč / " + t("month")).
    setOut(root, 'efiRent', num(r.efiRent));
    setOut(root, 'grossYear', czk(Math.round(r.grossYear)));
    setOut(root, 'netYear', czk(Math.round(r.netYear)));
    setOut(root, 'grossYield', pct(r.grossYield));
    setOut(root, 'netYield', pct(r.netYield));
    if (occInput) {
      setOut(root, 'occLabel', `${num(occInput.value)}${pctSpace()}%`);
    }
  };

  unitSelect.addEventListener('change', () => {
    customRent = null;
    render();
  });
  modeInputs.forEach((input) => input.addEventListener('change', render));
  if (rentInput) {
    rentInput.addEventListener('input', (event) => {
      customRent = event.target.value === '' ? null : Number(event.target.value);
      render();
    });
  }
  if (adrInput) {
    adrInput.addEventListener('input', render);
  }
  if (occInput) {
    occInput.addEventListener('input', render);
  }

  render();
}

export default function initRealityCalc() {
  const root = document.querySelector('[data-re-calc]');
  const config = window.EFI_REALITY_CALC;

  if (!root || !config || !Array.isArray(config.units) || config.units.length === 0) {
    return;
  }

  const params = config.params || {};

  if (config.mode === 'growth') {
    initGrowth(root, params, config.units);
  } else if (config.mode === 'rent') {
    initRent(root, params, config.units);
  }
}
